/**
 * A reader for the Compound File Binary format (MS-CFB), the OLE2 container
 * that HWP 5.0 and the legacy `.doc`/`.xls`/`.ppt` formats are built on.
 *
 * A compound file is a FAT filesystem inside one file: a header, a sector-
 * allocation table (the FAT), a directory of named streams and storages
 * arranged as a red-black tree, and — for small streams — a second, finer
 * allocation (the mini-FAT) over a "mini stream" that itself lives in the FAT.
 * Reading a stream means following one of those two chains.
 *
 * This reads; it does not write, and it validates rather than trusts. A sector
 * pointer that leaves the file, a chain that loops, or a directory that names
 * more entries than the file could hold are all how a malformed or hostile
 * compound file makes a naive reader loop or read out of bounds — HWP in
 * particular has a long history as a malware carrier (§30) — so every chain is
 * bounded by the number of sectors that actually exist.
 */
import { HandlerError } from '../contract.js';

const SIGNATURE = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1];
const ENDOFCHAIN = 0xfffffffe;
const FREESECT = 0xffffffff;
const NOSTREAM = 0xffffffff;

export interface CfbEntry {
  readonly name: string;
  /** 1 = storage, 2 = stream, 5 = root. */
  readonly type: number;
  readonly startSector: number;
  readonly size: number;
  readonly left: number;
  readonly right: number;
  readonly child: number;
}

export interface CfbFile {
  /** Every stream, addressed by its path (`BodyText/Section0`). */
  readonly streams: ReadonlyMap<string, CfbEntry>;
  readStream(entry: CfbEntry): Uint8Array;
  /** Convenience: read a stream by path, or throw. */
  read(path: string): Uint8Array;
  has(path: string): boolean;
}

export function openCompoundFile(bytes: Uint8Array): CfbFile {
  if (bytes.length < 512) throw new HandlerError('error.damagedOrIncomplete', 'file is too small to be a compound file');
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  for (let i = 0; i < 8; i++) {
    if (bytes[i] !== SIGNATURE[i]) throw new HandlerError('error.damagedOrIncomplete', 'not a compound file (bad signature)');
  }

  const sectorShift = view.getUint16(30, true);
  const sectorSize = 1 << sectorShift;             // 512 (v3) or 4096 (v4)
  const miniSectorShift = view.getUint16(32, true);
  const miniSectorSize = 1 << miniSectorShift;     // 64
  const numFatSectors = view.getUint32(44, true);
  const firstDirSector = view.getUint32(48, true);
  const miniCutoff = view.getUint32(56, true);     // 4096
  const firstMiniFatSector = view.getUint32(60, true);
  const numMiniFatSectors = view.getUint32(64, true);
  const firstDifatSector = view.getUint32(68, true);
  const numDifatSectors = view.getUint32(72, true);

  const totalSectors = Math.max(0, Math.floor((bytes.length - 512) / sectorSize));
  const sectorOffset = (s: number): number => 512 + s * sectorSize;

  const sectorBytes = (s: number): Uint8Array => {
    if (s < 0 || s >= totalSectors) {
      throw new HandlerError('error.damagedOrIncomplete', `sector ${s} is outside the file`);
    }
    return bytes.subarray(sectorOffset(s), sectorOffset(s) + sectorSize);
  };

  /* ---- the FAT ------------------------------------------------------ */

  // DIFAT: the first 109 FAT-sector locations live in the header; any more are
  // chained through DIFAT sectors. Most real files use only the header array.
  const fatSectorList: number[] = [];
  for (let i = 0; i < 109 && fatSectorList.length < numFatSectors; i++) {
    const s = view.getUint32(76 + i * 4, true);
    if (s === FREESECT || s === ENDOFCHAIN) break;
    fatSectorList.push(s);
  }
  let difatSector = firstDifatSector;
  const perDifat = sectorSize / 4 - 1;
  for (let guard = 0; guard < numDifatSectors + 1 && difatSector !== ENDOFCHAIN && difatSector !== FREESECT && fatSectorList.length < numFatSectors; guard++) {
    const sec = sectorBytes(difatSector);
    const sv = new DataView(sec.buffer, sec.byteOffset, sec.byteLength);
    for (let i = 0; i < perDifat && fatSectorList.length < numFatSectors; i++) {
      const s = sv.getUint32(i * 4, true);
      if (s !== FREESECT && s !== ENDOFCHAIN) fatSectorList.push(s);
    }
    difatSector = sv.getUint32(perDifat * 4, true);
  }

  const fat = new Uint32Array(fatSectorList.length * (sectorSize / 4));
  fatSectorList.forEach((s, k) => {
    const sec = sectorBytes(s);
    const sv = new DataView(sec.buffer, sec.byteOffset, sec.byteLength);
    for (let i = 0; i < sectorSize / 4; i++) fat[k * (sectorSize / 4) + i] = sv.getUint32(i * 4, true);
  });

  /** Follow a FAT chain, bounded by the sector count so a loop cannot hang. */
  const chain = (start: number, table: Uint32Array): number[] => {
    const out: number[] = [];
    let s = start;
    const seen = new Set<number>();
    while (s !== ENDOFCHAIN && s !== FREESECT && out.length <= table.length) {
      if (seen.has(s)) throw new HandlerError('error.damagedOrIncomplete', 'sector chain loops');
      seen.add(s);
      out.push(s);
      s = table[s] ?? ENDOFCHAIN;
    }
    return out;
  };

  const readChain = (start: number, size: number, table: Uint32Array, unit: number, reader: (s: number) => Uint8Array): Uint8Array => {
    const out = new Uint8Array(size);
    let written = 0;
    for (const s of chain(start, table)) {
      if (written >= size) break;
      const piece = reader(s);
      const n = Math.min(unit, size - written);
      out.set(piece.subarray(0, n), written);
      written += n;
    }
    return out;
  };

  /* ---- the directory ------------------------------------------------ */

  const dirSectors = chain(firstDirSector, fat);
  const entries: CfbEntry[] = [];
  for (const s of dirSectors) {
    const sec = sectorBytes(s);
    const dv = new DataView(sec.buffer, sec.byteOffset, sec.byteLength);
    for (let off = 0; off + 128 <= sec.length; off += 128) {
      const nameLen = dv.getUint16(off + 64, true);
      const type = dv.getUint8(off + 66);
      if (type === 0) { entries.push(EMPTY); continue; } // unallocated slot
      let name = '';
      for (let i = 0; i < Math.max(0, nameLen - 2); i += 2) name += String.fromCharCode(dv.getUint16(off + i, true));
      entries.push({
        name,
        type,
        left: dv.getUint32(off + 68, true),
        right: dv.getUint32(off + 72, true),
        child: dv.getUint32(off + 76, true),
        startSector: dv.getUint32(off + 116, true),
        // Stream size is 64-bit; a stream over 4 GiB cannot fit in memory here.
        size: dv.getUint32(off + 120, true),
      });
    }
  }
  if (entries.length === 0 || !entries[0] || entries[0].type !== 5) {
    throw new HandlerError('error.damagedOrIncomplete', 'compound file has no root storage');
  }
  const root = entries[0];

  /* ---- the mini stream and mini-FAT --------------------------------- */

  const miniFatSectors = numMiniFatSectors > 0 ? chain(firstMiniFatSector, fat) : [];
  const miniFat = new Uint32Array(miniFatSectors.length * (sectorSize / 4));
  miniFatSectors.forEach((s, k) => {
    const sec = sectorBytes(s);
    const sv = new DataView(sec.buffer, sec.byteOffset, sec.byteLength);
    for (let i = 0; i < sectorSize / 4; i++) miniFat[k * (sectorSize / 4) + i] = sv.getUint32(i * 4, true);
  });
  // The mini stream is the root entry's data, read from the ordinary FAT.
  const miniStream = root.size > 0 ? readChain(root.startSector, root.size, fat, sectorSize, sectorBytes) : new Uint8Array(0);
  const miniSectorBytes = (s: number): Uint8Array =>
    miniStream.subarray(s * miniSectorSize, s * miniSectorSize + miniSectorSize);

  const readStream = (entry: CfbEntry): Uint8Array => {
    if (entry.size === 0) return new Uint8Array(0);
    return entry.size < miniCutoff
      ? readChain(entry.startSector, entry.size, miniFat, miniSectorSize, miniSectorBytes)
      : readChain(entry.startSector, entry.size, fat, sectorSize, sectorBytes);
  };

  /* ---- walk the directory tree into paths --------------------------- */

  const streams = new Map<string, CfbEntry>();
  const visited = new Set<number>();
  const walk = (index: number, prefix: string): void => {
    if (index === NOSTREAM || index >= entries.length || visited.has(index)) return;
    visited.add(index);
    const entry = entries[index];
    if (!entry || entry.type === 0) return;
    // In-order over the red-black tree: left and right are siblings, child is
    // the first entry of a storage's contents.
    walk(entry.left, prefix);
    const path = prefix ? `${prefix}/${entry.name}` : entry.name;
    if (entry.type === 2) streams.set(path, entry);
    if (entry.type === 1 || entry.type === 5) walk(entry.child, entry.type === 5 ? '' : path);
    walk(entry.right, prefix);
  };
  walk(root.child, '');

  return {
    streams,
    readStream,
    has: (path) => streams.has(path),
    read(path) {
      const entry = streams.get(path);
      if (!entry) throw new HandlerError('error.damagedOrIncomplete', `no stream: ${path}`);
      return readStream(entry);
    },
  };
}

const EMPTY: CfbEntry = { name: '', type: 0, startSector: 0, size: 0, left: NOSTREAM, right: NOSTREAM, child: NOSTREAM };
